import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Button, Modal } from "@cpnw/ui";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useSortableTable } from "components/DashboardPassportTable/useSortableTable";
import { DashboardItemsPerPage } from "components/DashboardItemsPerPage";
import { DashboardContactInformationModal } from "components/DashboardContactInformationModal";
import DashboardPassportItem from "components/DashboardPassportItem";
import DashboardTablePagination from "components/DashboardTablePagination";
import DashboardSearch from "components/DashboardSearch";
import DashboardPassportFileModal from "components/DashboardPassportFileModal";
import DashboardPassportReportItemModal from "components/DashboardPassportReportItemModal";
import DashboardPassportTableKey from "components/DashboardPassportTableKey";
import DashboardSelect from "components/DashboardSelect";
import { format, isValid, parseISO } from "date-fns";

import styles from "./DashboardPassportTable.module.css";
import RequiredByDropdownED from "components/RequiredByDropdownED";

export function DashboardPassportTable({
  className,
  data,
  filters,
  columns,
  assignmentReview,
  passportUpload,
  passportDelete,
  onGetPassportsData,
  onDownloadContactPassports,
  showDelete,
  showDownload,
  showUpload,
  userRoles,
}) {
  const [videoUrl, setVideoUrl] = useState(undefined);
  const [isOpenVideoTutorialModal, setOpenVideoTutorialModal] =
    useState(false);

  const VideoTutorialModal = ({ isOpen, videoUrl }) => {
    const closeModal = () => {
      onCloseVideoTutorialModal();
    };

    return (
      <div>
        <Modal
          isOpen={isOpen}
          onRequestClose={closeModal}
          className={styles.customModal}
          modalClassName={styles.modalBox}
          children={
            <>
              <div className={styles.modal_row}>
                <FontAwesomeIcon
                  icon={faXmark}
                  style={{
                    fontSize: "28px",
                    transform:
                      "translateY(-15px) translateX(15px)",
                    color: "#dddddd",
                    cursor: "pointer",
                  }}
                  onClick={closeModal}
                />
              </div>

              <div className={styles.videoWrapper}>
                <iframe
                  src={videoUrl}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
            </>
          }
        />
      </div>
    );
  };

  /*
   * ============================================================
   * TABLE DATA
   * ============================================================
   */

  const [tableData, setTableData, handleSorting] =
    useSortableTable(data, columns);

  const [filterName, setFilterName] = useState("");
  const [dataToShow, setDataToShow] = useState(tableData);

  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);

  const [totalItems, setTotalItems] = useState(
    tableData?.length || 0
  );

  const [isShowSelected, setIsShowSelected] = useState(false);

  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 25, 50, 100];

  const startIndex = (currentPage - 1) * limit;
  const endIndex = Math.min(
    startIndex + limit - 1,
    dataToShow.length - 1
  );

  const currentDisplayedItems = dataToShow.slice(
    startIndex,
    endIndex + 1
  );

  const allDisplayedSelected =
    currentDisplayedItems.length > 0 &&
    currentDisplayedItems.every((item) =>
      selectedItems.includes(item.id)
    );

  const handleSelectAll = () => {
    const displayedIds = currentDisplayedItems.map(
      (item) => item.id
    );

    if (allDisplayedSelected) {
      setSelectedItems((prev) =>
        prev.filter((id) => !displayedIds.includes(id))
      );
    } else {
      setSelectedItems((prev) => [
        ...prev,
        ...displayedIds.filter((id) => !prev.includes(id)),
      ]);
    }
  };

  const checkboxRef = useRef(null);

  const someDisplayedSelected =
    currentDisplayedItems.some((item) =>
      selectedItems.includes(item.id)
    ) && !allDisplayedSelected;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate =
        someDisplayedSelected;
    }
  }, [someDisplayedSelected]);

  /*
   * ============================================================
   * MODALS
   * ============================================================
   */

  const [currentItemInfo, setCurrentItemInfo] = useState();
  const [reviewAssignmentId, setReviewAssignmentId] =
    useState(false);

  const [isContactInfoModalOpen, setIsContactInfoModalOpen] =
    useState(false);

  const [
    isFileManagementModalOpen,
    setIsFileManagementModalOpen,
  ] = useState(false);

  const [dataLearningModuleModal, setDataLearningModuleModal] =
    useState([]);

  const [
    isSummaryReportItemModalOpen,
    setIsSummaryReportItemModalOpen,
  ] = useState(false);

  const [
    contactDataWithPassports,
    setContactDataWithPassports,
  ] = useState(null);

  const [
    passportFileModalLoading,
    setPassportFileModalLoading,
  ] = useState(false);

  /*
   * ============================================================
   * FILTER OPTIONS
   * ============================================================
   */

  const [locationsOptions, setLocationsOptions] = useState([]);
  const [schoolsOptions, setSchoolsOptions] = useState([]);
  const [startDatesOptions, setStartDatesOptions] = useState([]);

  const [requiredByEDOptions, setRequiredByEDOptions] =
    useState([]);

  /*
   * IMPORTANT:
   *
   * selectedPrograms contains the values returned by
   * RequiredByDropdownED.
   *
   * Example:
   *
   * [
   *   {
   *     Id: 916,
   *     Name: "TESTING PRE LICENSURE NURSING CLINICAL"
   *   }
   * ]
   */
  const [selectedPrograms, setSelectedPrograms] = useState([]);

  const [selectedLocation, setSelectedLocation] =
    useState(null);

  const [selectedStartDate, setSelectedStartDate] =
    useState(null);

  /*
   * ============================================================
   * FORMAT SCHOOL / PROGRAM OPTIONS
   * ============================================================
   */

  const formatSchoolProgramOptions = (schools = []) => {
    return schools.flatMap((school) =>
      (school?.programs || []).map((program) => ({
        schoolId: school.facilityId,
        schoolName: school.facilityName,
        programId: program.businessUnitId,
        programName: program.businessUnitName,
      }))
    );
  };

  useEffect(() => {
    const optionsByProgramId = new Map();

    for (const item of data || []) {
      const programId = item?.contact?.programId;
      const schoolName = item?.contact?.school;
      const programName = item?.contact?.program;
      if (!programId || !schoolName || !programName) continue;
      optionsByProgramId.set(programId, {
        schoolId: schoolName,
        schoolName,
        programId,
        programName,
      });
    }

    setRequiredByEDOptions(
      [...optionsByProgramId.values()].sort((a, b) =>
        `${a.schoolName || ""}|${a.programName || ""}`.localeCompare(
          `${b.schoolName || ""}|${b.programName || ""}`
        )
      )
    );
  }, [data]);

  /*
   * ============================================================
   * ASSIGNMENT HELPERS
   * ============================================================
   */

  const getItemAssignments = (item) => {
    return [
      ...(Array.isArray(item?.Assignments)
        ? item.Assignments
        : []),

      ...(item?.Assignment
        ? [item.Assignment]
        : []),
    ];
  };

  const formatOptionDate = (rawDate) => {
    if (!rawDate) return null;

    // Assignment start dates are calendar dates. Parsing a midnight UTC value
    // as a local Date shifts it to the previous day in US time zones.
    if (typeof rawDate === "string") {
      const dateOnlyMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (dateOnlyMatch) {
        const [, year, month, day] = dateOnlyMatch;
        return `${month}/${day}/${year}`;
      }
    }

    const dateObj =
      typeof rawDate === "string"
        ? parseISO(rawDate)
        : new Date(rawDate);

    return isValid(dateObj)
      ? format(dateObj, "MM/dd/yyyy")
      : null;
  };

  const getItemLocations = (item) => {
    // ED mode: AssignmentFilterData contains per-assignment HC location
    const filterData = item?.AssignmentFilterData || [];
    if (filterData.length > 0) {
      return [
        ...new Set(
          filterData.map((a) => a.HCFacilityName).filter(Boolean)
        ),
      ];
    }
    // HC mode: location is directly on the contact
    const contactLocation = item?.contact?.location;
    return contactLocation ? [contactLocation] : [];
  };

  const getItemStartDates = (item) => {
    // ED mode: AssignmentFilterData contains per-assignment start dates
    const filterData = item?.AssignmentFilterData || [];
    if (filterData.length > 0) {
      return [
        ...new Set(
          filterData
            .map((a) => formatOptionDate(a.StartDate))
            .filter(Boolean)
        ),
      ];
    }
    // HC mode: start date is directly on the contact
    const contactStartDate = formatOptionDate(item?.contact?.startDate);
    return contactStartDate ? [contactStartDate] : [];
  };

  /*
   * ============================================================
   * LOCATION / DATE MATCHING
   * ============================================================
   */

  const itemMatchesAssignmentFilters = (
    item,
    location,
    startDate
  ) => {
    if (!location && !startDate) {
      return true;
    }

    // ED mode: match against AssignmentFilterData entries
    const filterData = item?.AssignmentFilterData || [];
    if (filterData.length > 0) {
      return filterData.some((a) => {
        const matchesLocation =
          !location || a.HCFacilityName === location;
        const matchesDate =
          !startDate || formatOptionDate(a.StartDate) === startDate;
        return matchesLocation && matchesDate;
      });
    }

    // HC mode: match directly against contact location / start date
    const itemLocations = getItemLocations(item);
    const itemStartDates = getItemStartDates(item);
    const matchesLocation = !location || itemLocations.includes(location);
    const matchesDate = !startDate || itemStartDates.includes(startDate);
    return matchesLocation && matchesDate;
  };

  /*
   * ============================================================
   * BUILD FILTER OPTIONS FROM DATA
   * ============================================================
   */

  useEffect(() => {
    if (!data || data.length === 0) {
      setLocationsOptions([]);
      setSchoolsOptions([]);
      setStartDatesOptions([]);
      return;
    }

    /*
     * Locations
     */
    const locations = data.flatMap((item) =>
      getItemLocations(item)
    );

    const uniqueLocations = [
      ...new Set(locations),
    ]
      .filter(Boolean)
      .sort();

    setLocationsOptions(uniqueLocations);

    /*
     * Schools
     */
    const schools = data
      .map(
        (item) =>
          item?.contact?.school
      )
      .filter(Boolean);

    const uniqueSchools = [
      ...new Set(schools),
    ].sort();

    setSchoolsOptions(uniqueSchools);

    /*
     * Start Dates
     */
    const dates = data.flatMap((item) =>
      getItemStartDates(item)
    );

    const uniqueDates = [
      ...new Set(dates),
    ].sort(
      (a, b) =>
        new Date(a) - new Date(b)
    );

    setStartDatesOptions(uniqueDates);
  }, [data]);

  /*
   * ============================================================
   * MAIN FILTER FUNCTION
   * ============================================================
   *
   * IMPORTANT:
   *
   * Always filter from `data`, NOT `tableData`.
   *
   * `data` = original report data.
   *
   * This prevents filters from becoming permanently stacked
   * and allows the user to remove/change filters correctly.
   */

  const handleFilter = (
    location,
    programs,
    startDate
  ) => {
    // Match by BusinessUnitId, which is unique per program across all schools
    const selectedProgramIds = new Set(
      (programs || []).map((p) => Number(p.Id)).filter(Number.isFinite)
    );

    const filteredData = (data || []).filter((item) => {
      const matchesProgram =
        selectedProgramIds.size === 0 ||
        selectedProgramIds.has(Number(item?.contact?.programId));

      const matchesAssignment = itemMatchesAssignmentFilters(
        item,
        location,
        startDate
      );

      return matchesProgram && matchesAssignment;
    });

    setTableData(filteredData);
    setCurrentPage(1);
  };

  // Reapply active filters after a report refresh. The sortable-table hook
  // receives the new source rows first and would otherwise show all rows until
  // the user changes a filter again.
  useEffect(() => {
    handleFilter(
      selectedLocation,
      selectedPrograms,
      selectedStartDate
    );
  }, [data]);

  /*
   * ============================================================
   * FILTER HANDLERS
   * ============================================================
   */

  const onSelectLocation = (value) => {
    setSelectedLocation(value);

    handleFilter(
      value,
      selectedPrograms,
      selectedStartDate
    );
  };

  const onSelectSchool = (value) => {
    const programs = value || [];

    setSelectedPrograms(programs);

    handleFilter(
      selectedLocation,
      programs,
      selectedStartDate
    );
  };

  const onSelectStartDate = (value) => {
    setSelectedStartDate(value);

    handleFilter(
      selectedLocation,
      selectedPrograms,
      value
    );
  };

  /*
   * ============================================================
   * SEARCH / SELECTED FILTER
   * ============================================================
   */

  useEffect(() => {
    const newData = (
      tableData || []
    )
      .filter((item) => {
        return (
          item?.contact?.name
            ?.toLowerCase()
            .includes(
              filterName.toLowerCase()
            )
        );
      })
      .filter((item) => {
        if (isShowSelected) {
          return selectedItems.includes(
            item.id
          );
        }

        return true;
      });

    setTotalItems(newData.length);
    setDataToShow(newData);
  }, [
    tableData,
    filterName,
    selectedItems,
    isShowSelected,
  ]);

  /*
   * ============================================================
   * SORTING
   * ============================================================
   */

  const handleSortingChange = (accessor) => {
    const sortOrder =
      accessor === sortField &&
      order === "asc"
        ? "desc"
        : "asc";

    setSortField(accessor);
    setOrder(sortOrder);

    handleSorting(
      accessor,
      sortOrder
    );
  };

  /*
   * ============================================================
   * PAGINATION
   * ============================================================
   */

  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
  };

  /*
   * ============================================================
   * EXCEL EXPORT HELPERS
   * ============================================================
   */

  const formatDataHeaders = (item) => {
    return {
      requirementId:
        item.RequirementId,
      requirementName:
        item.Name,
      categoryName:
        item.CategoryName,
      subCategoryName:
        item.SubCategoryName,
      value: item.Name,
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    try {
      let date;

      if (
        typeof dateString ===
          "string" &&
        dateString.includes("-")
      ) {
        const parts = dateString
          .split("T")[0]
          .split("-");

        if (parts.length === 3) {
          date = new Date(
            parseInt(parts[0]),
            parseInt(parts[1]) - 1,
            parseInt(parts[2])
          );
        } else {
          date = parseISO(
            dateString
          );
        }
      } else if (
        typeof dateString ===
          "string" &&
        dateString.includes("/")
      ) {
        return dateString;
      } else {
        date = new Date(
          dateString
        );
      }

      if (isValid(date)) {
        return format(
          date,
          "MM/dd/yyyy"
        );
      }

      return dateString;
    } catch (err) {
      console.warn(
        "Error formatting date:",
        dateString,
        err
      );

      return dateString;
    }
  };

  /*
   * ============================================================
   * REQUIREMENT FORMATTER
   * ============================================================
   */

  const formatRequirementMultilineContent = (
    req,
    header
  ) => {
    if (!req) {
      return "";
    }

    const lines = [];

    const categoryOrSubCategoryName =
      header.subCategoryName ||
      header.categoryName;

    const status =
      req.ContactRequirementStatus ||
      "";

    if (status) {
      lines.push(
        `Status: ${status}`
      );
    }

    const submission =
      req.ContactRequirementSubmissions &&
      req.ContactRequirementSubmissions
        .length > 0
        ? req.ContactRequirementSubmissions[0]
        : null;

    if (
      submission &&
      submission.JSONContent &&
      submission.JSONContent.length > 0
    ) {
      let content;

      try {
        content = JSON.parse(
          submission.JSONContent
        );
      } catch (error) {
        console.warn(
          "Invalid JSONContent:",
          error
        );

        content = null;
      }

      if (content) {
        /*
         * TB
         */
        if (
          categoryOrSubCategoryName ===
          "TB"
        ) {
          if (
            content.type === "A"
          ) {
            lines.push(
              "A: Two-Step TB: TRUE"
            );

            if (
              content.test1?.placeDate
            ) {
              lines.push(
                `Test 1 Place Date: ${formatDate(
                  content.test1.placeDate
                )}`
              );
            }

            if (
              content.test1?.readDate
            ) {
              lines.push(
                `Test 1 Read Date: ${formatDate(
                  content.test1.readDate
                )}`
              );
            }

            if (
              content.test1?.result
            ) {
              lines.push(
                `Test 1 Result: ${content.test1.result}`
              );
            }

            if (
              content.test2?.placeDate
            ) {
              lines.push(
                `Test 2 Place Date: ${formatDate(
                  content.test2.placeDate
                )}`
              );
            }

            if (
              content.test2?.readDate
            ) {
              lines.push(
                `Test 2 Read Date: ${formatDate(
                  content.test2.readDate
                )}`
              );
            }

            if (
              content.test2?.result
            ) {
              lines.push(
                `Test 2 Result: ${content.test2.result}`
              );
            }
          } else {
            lines.push(
              "A: Two-Step TB: FALSE"
            );
          }

          if (
            content.type === "B"
          ) {
            lines.push(
              "B: TB/IGRA bloodtest: TRUE"
            );

            if (
              content.dateDrawn
            ) {
              lines.push(
                `Date Drawn: ${formatDate(
                  content.dateDrawn
                )}`
              );
            }

            if (
              content.result
            ) {
              lines.push(
                `Result: ${content.result}`
              );
            }
          } else {
            lines.push(
              "B: TB/IGRA bloodtest: FALSE"
            );
          }

          if (
            content.type === "C"
          ) {
            lines.push(
              "C: If Past, or new positive, history of BCG vaccine: TRUE"
            );

            if (
              content.xRayDate
            ) {
              lines.push(
                `X-Ray Date: ${formatDate(
                  content.xRayDate
                )}`
              );
            }

            if (
              content.result
            ) {
              lines.push(
                `Result: ${content.result}`
              );
            }
          } else {
            lines.push(
              "C: If Past, or new positive, history of BCG vaccine: FALSE"
            );
          }
        }

        /*
         * HepB(new)
         */
        else if (
          categoryOrSubCategoryName ===
          "HepB(new)"
        ) {
          if (
            content.type === "A"
          ) {
            if (
              content.A?.serie1
                ?.type === "2-Step"
            ) {
              lines.push(
                "A: 2-Step Series"
              );

              if (
                content.A.serie1
                  .twoStepDate1
              ) {
                lines.push(
                  `Vaccination Date 1: ${formatDate(
                    content.A.serie1.twoStepDate1
                  )}`
                );
              }

              if (
                content.A.serie1
                  .twoStepDate2
              ) {
                lines.push(
                  `Vaccination Date 2: ${formatDate(
                    content.A.serie1.twoStepDate2
                  )}`
                );
              }

              if (
                content.A.serie1
                  .twoStepDateDrawn
              ) {
                lines.push(
                  `Titer Date Drawn: ${formatDate(
                    content.A.serie1.twoStepDateDrawn
                  )}`
                );
              }

              if (
                content.A.serie1
                  .twoStepResult
              ) {
                lines.push(
                  `Titer Result: ${content.A.serie1.twoStepResult}`
                );
              }
            } else if (
              content.A?.serie1
                ?.type === "3-Step"
            ) {
              lines.push(
                "A: 3-Step Series"
              );

              if (
                content.A.serie1
                  .threeStepDate1
              ) {
                lines.push(
                  `Vaccination Date 1: ${formatDate(
                    content.A.serie1.threeStepDate1
                  )}`
                );
              }

              if (
                content.A.serie1
                  .threeStepDate2
              ) {
                lines.push(
                  `Vaccination Date 2: ${formatDate(
                    content.A.serie1.threeStepDate2
                  )}`
                );
              }

              if (
                content.A.serie1
                  .threeStepDate3
              ) {
                lines.push(
                  `Vaccination Date 3: ${formatDate(
                    content.A.serie1.threeStepDate3
                  )}`
                );
              }

              if (
                content.A.serie1
                  .threeStepDateDrawn
              ) {
                lines.push(
                  `Titer Date Drawn: ${formatDate(
                    content.A.serie1.threeStepDateDrawn
                  )}`
                );
              }

              if (
                content.A.serie1
                  .threeStepResult
              ) {
                lines.push(
                  `Titer Result: ${content.A.serie1.threeStepResult}`
                );
              }
            }
          } else if (
            content.type === "B"
          ) {
            lines.push(
              "B: Proof of Immunity"
            );

            if (
              content.B?.proofs
                ?.length > 0
            ) {
              if (
                content.B.proofs[0]
                  .date
              ) {
                lines.push(
                  `B: Proof of Immunity -3 var Date: ${formatDate(
                    content.B.proofs[0].date
                  )}`
                );
              }

              if (
                content.B.proofs[0]
                  .result
              ) {
                lines.push(
                  `B: Titer- Date/Results: ${content.B.proofs[0].result}`
                );
              }
            }
          } else if (
            content.type === "C"
          ) {
            lines.push(
              "C: Signed Series in Process"
            );

            if (
              content.C?.processDate
                ?.date
            ) {
              lines.push(
                `Date: ${formatDate(
                  content.C.processDate.date
                )}`
              );
            }
          } else if (
            content.type === "D"
          ) {
            lines.push(
              "D: History of disease"
            );
          } else if (
            content.type === "E"
          ) {
            lines.push(
              "E: Non-converter"
            );
          }
        }

        /*
         * MMR/MMRV(new)
         */
        else if (
          categoryOrSubCategoryName ===
          "MMR/MMRV(new)"
        ) {
          if (
            content.type === "A"
          ) {
            if (
              content.date1
            ) {
              lines.push(
                `A: Vaccination Date 1: ${formatDate(
                  content.date1
                )}`
              );
            }

            if (
              content.date2
            ) {
              lines.push(
                `A: Vaccination Date 2: ${formatDate(
                  content.date2
                )}`
              );
            }
          } else if (
            content.type === "B"
          ) {
            if (
              content.measles
                ?.length > 0
            ) {
              if (
                content.measles[0]
                  .date
              ) {
                lines.push(
                  `B: Immunity by Titer Measles- Date: ${formatDate(
                    content.measles[0].date
                  )}`
                );
              }

              if (
                content.measles[0]
                  .result
              ) {
                lines.push(
                  `B: Immunity by Titer Measles- Result: ${content.measles[0].result}`
                );
              }
            }

            if (
              content.mumps
                ?.length > 0
            ) {
              if (
                content.mumps[0]
                  .date
              ) {
                lines.push(
                  `B: Immunity by Titer Mumps- Date: ${formatDate(
                    content.mumps[0].date
                  )}`
                );
              }

              if (
                content.mumps[0]
                  .result
              ) {
                lines.push(
                  `B: Immunity by Titer Mumps- Result: ${content.mumps[0].result}`
                );
              }
            }

            if (
              content.rubella
                ?.length > 0
            ) {
              if (
                content.rubella[0]
                  .date
              ) {
                lines.push(
                  `B: Immunity by Titer Rubella- Date: ${formatDate(
                    content.rubella[0].date
                  )}`
                );
              }

              if (
                content.rubella[0]
                  .result
              ) {
                lines.push(
                  `B: Immunity by Titer Rubella- Result: ${content.rubella[0].result}`
                );
              }
            }
          }
        }

        /*
         * Varicella(new)
         */
        else if (
          categoryOrSubCategoryName ===
          "Varicella(new)"
        ) {
          if (
            content.type === "A"
          ) {
            if (
              content.A?.date?.date1
            ) {
              lines.push(
                `A: Varicella Titer Date: ${formatDate(
                  content.A.date.date1
                )}`
              );
            }

            if (
              content.A?.date?.date2
            ) {
              lines.push(
                `A: Date 2: ${formatDate(
                  content.A.date.date2
                )}`
              );
            }
          } else if (
            content.type === "B"
          ) {
            if (
              content.B
                ?.vaccinationDates
                ?.length > 0
            ) {
              if (
                content.B
                  .vaccinationDates[0]
                  .date
              ) {
                lines.push(
                  `B: Varicella Titer Date: ${formatDate(
                    content.B.vaccinationDates[0].date
                  )}`
                );
              }

              if (
                content.B
                  .vaccinationDates[0]
                  .result
              ) {
                lines.push(
                  `B: Varicella Result: ${content.B.vaccinationDates[0].result}`
                );
              }
            }
          }
        }

        /*
         * Tdap
         */
        else if (
          categoryOrSubCategoryName ===
            "Tdap(new)" ||
          categoryOrSubCategoryName ===
            "Tdap"
        ) {
          if (
            content.date1
          ) {
            lines.push(
              `Initial Tdap date: ${formatDate(
                content.date1
              )}`
            );
          }

          if (
            content.date2
          ) {
            lines.push(
              `Td/Tdap subsequent dose date: ${formatDate(
                content.date2
              )}`
            );
          }
        }

        /*
         * COVID-19(New)
         */
        else if (
          categoryOrSubCategoryName ===
          "Covid-19(New)"
        ) {
          if (content.na) {
            lines.push(
              "N/A: Selected"
            );
          } else {
            if (
              content.manufacturer
            ) {
              lines.push(
                `Manufacturer: ${content.manufacturer}`
              );
            }

            if (
              content.boosterList
                ?.boosterList
                ?.length > 0
            ) {
              if (
                content.boosterList
                  .boosterList[0]
                  ?.date
              ) {
                lines.push(
                  `Dose 1: ${formatDate(
                    content.boosterList.boosterList[0].date
                  )}`
                );
              }

              if (
                content.boosterList
                  .boosterList[1]
                  ?.date
              ) {
                lines.push(
                  `Dose 2: ${formatDate(
                    content.boosterList.boosterList[1].date
                  )}`
                );
              }

              if (
                content.boosterList
                  .boosterList
                  .length > 2 &&
                content.boosterList
                  .boosterList[
                  content.boosterList
                    .boosterList
                    .length - 1
                ]?.date
              ) {
                lines.push(
                  `Booster - Dose 2 Date: ${formatDate(
                    content.boosterList.boosterList[
                      content.boosterList.boosterList.length -
                        1
                    ].date
                  )}`
                );
              }
            }
          }
        }

        /*
         * Influenza
         */
        else if (
          categoryOrSubCategoryName ===
          "Influenza"
        ) {
          if (
            req.FrequencyId == 3 &&
            req.ContactRequirementExpiresOn
          ) {
            lines.push(
              `Seasonal Expiration Date: ${formatDate(
                req.ContactRequirementExpiresOn
              )}`
            );
          }

          if (
            content.vaccineDates
              ?.length > 0
          ) {
            content.vaccineDates.forEach(
              (vd, idx) => {
                if (
                  vd.immunizationDate
                ) {
                  lines.push(
                    `Immunization Date-${idx + 1}: ${formatDate(
                      vd.immunizationDate
                    )}`
                  );
                }

                if (vd.location) {
                  lines.push(
                    `Provider & Location-${idx + 1}: ${vd.location}`
                  );
                }
              }
            );
          }
        }

        /*
         * BLS
         */
        else if (
          categoryOrSubCategoryName ===
          "BLS"
        ) {
          if (
            content.issueDate
          ) {
            lines.push(
              `Issue Date: ${formatDate(
                content.issueDate
              )}`
            );
          }
        }

        /*
         * Criminal History Disclosure
         */
        else if (
          categoryOrSubCategoryName ===
          "Criminal History Disclosure"
        ) {
          if (
            content
              .disclosureSignatureDates
              ?.length > 0
          ) {
            const latestDate =
              content
                .disclosureSignatureDates[
                content
                  .disclosureSignatureDates
                  .length - 1
              ]
                .dateOfDisclosureSignature;

            if (latestDate) {
              lines.push(
                `Date of Disclosure Signature: ${formatDate(
                  latestDate
                )}`
              );
            }
          }
        }

        /*
         * Background / WATCH
         */
        else if (
          categoryOrSubCategoryName ===
            "Checkr Background Check" ||
          categoryOrSubCategoryName ===
            "CPNW WATCH" ||
          categoryOrSubCategoryName ===
            "Independent Background Check" ||
          categoryOrSubCategoryName ===
            "Independent WATCH"
        ) {
          if (
            content.reportCompletedOn
          ) {
            lines.push(
              `Report Completed On: ${formatDate(
                content.reportCompletedOn
              )}`
            );
          }
        }

        /*
         * Respirator
         */
        else if (
          categoryOrSubCategoryName ===
          "Respirator"
        ) {
          if (
            content.notAvailable
          ) {
            lines.push(
              "N/A: Selected"
            );
          } else {
            if (
              content.date1
            ) {
              lines.push(
                `Date Completed: ${formatDate(
                  content.date1
                )}`
              );
            }

            if (
              content.date2
            ) {
              lines.push(
                `Date Completed: ${formatDate(
                  content.date2
                )}`
              );
            }
          }
        }

        /*
         * Insurance
         */
        else if (
          categoryOrSubCategoryName ===
          "Insurance"
        ) {
          if (
            content.type === "A"
          ) {
            lines.push(
              "A: Insurance verified by program"
            );
          } else if (
            content.type === "B"
          ) {
            lines.push(
              "B: Individual Insurance"
            );
          } else if (
            content.type === "Military"
          ) {
            lines.push(
              "Military: Military immunization"
            );
          }
        }

        /*
         * Old Covid-19
         */
        else if (
          categoryOrSubCategoryName ===
          "Covid-19"
        ) {
          if (
            content.manufacturer
          ) {
            lines.push(
              `Manufacturer: ${content.manufacturer}`
            );
          }

          if (
            content.date1
          ) {
            lines.push(
              `Dose 1: ${formatDate(
                content.date1
              )}`
            );
          }

          if (
            content.date2
          ) {
            lines.push(
              `Dose 2: ${formatDate(
                content.date2
              )}`
            );
          }
        }

        /*
         * Old HepB
         */
        else if (
          categoryOrSubCategoryName ===
          "HepB"
        ) {
          if (
            content.type
          ) {
            lines.push(
              `Type: ${content.type}`
            );
          }

          if (
            content.date
          ) {
            lines.push(
              `Date: ${formatDate(
                content.date
              )}`
            );
          }

          if (
            content.result
          ) {
            lines.push(
              `Result: ${content.result}`
            );
          }
        }

        /*
         * Old Varicella
         */
        else if (
          categoryOrSubCategoryName ===
          "Varicella"
        ) {
          if (
            content.type ===
            "Military"
          ) {
            lines.push(
              "Military: Military immunization"
            );
          }

          if (
            content.date1
          ) {
            lines.push(
              `Date 1: ${formatDate(
                content.date1
              )}`
            );
          }

          if (
            content.date2
          ) {
            lines.push(
              `Date 2: ${formatDate(
                content.date2
              )}`
            );
          }

          if (
            content.varicellaDate
          ) {
            lines.push(
              `Varicella Titer Date: ${formatDate(
                content.varicellaDate
              )}`
            );
          }
        }

        /*
         * Old MMR
         */
        else if (
          categoryOrSubCategoryName ===
          "MMR/MMRV"
        ) {
          if (
            content.type ===
            "Military"
          ) {
            lines.push(
              "Military: Military immunization"
            );
          }

          if (
            content.date1
          ) {
            lines.push(
              `Date 1: ${formatDate(
                content.date1
              )}`
            );
          }

          if (
            content.date2
          ) {
            lines.push(
              `Date 2: ${formatDate(
                content.date2
              )}`
            );
          }

          if (
            content.measlesDate
          ) {
            lines.push(
              `Measles by titer: ${formatDate(
                content.measlesDate
              )}`
            );
          }

          if (
            content.mumpsDate
          ) {
            lines.push(
              `Mumps by titer: ${formatDate(
                content.mumpsDate
              )}`
            );
          }

          if (
            content.rubellaDate
          ) {
            lines.push(
              `Rubella by titer: ${formatDate(
                content.rubellaDate
              )}`
            );
          }

          if (
            content.result
          ) {
            lines.push(
              `Result: ${content.result}`
            );
          }
        }

        /*
         * Generic Immunization
         */
        else if (
          categoryOrSubCategoryName ===
          "Immunization"
        ) {
          if (
            content.immunizationDate
          ) {
            lines.push(
              `Immunization Date: ${formatDate(
                content.immunizationDate
              )}`
            );
          }

          if (
            content.location
          ) {
            lines.push(
              `Provider & Location: ${content.location}`
            );
          }
        }
      }
    }

    /*
     * Expiration
     */
    const expirationDate =
      req.ContactRequirementExpiresOn;

    if (expirationDate) {
      lines.push(
        `Expiration Date: ${formatDate(
          expirationDate
        )}`
      );
    }

    /*
     * Upload count
     */
    if (submission) {
      const fileCount =
        submission.FileCount || 0;

      lines.push(
        `Uploads: ${fileCount}docs`
      );
    } else {
      lines.push(
        "Uploads: 0docs"
      );
    }

    return lines.join("\n");
  };

  const formatDataValues = (
    req,
    header
  ) => {
    return {
      value:
        formatRequirementMultilineContent(
          req,
          header
        ),
      style: {},
    };
  };

  const uniqueByRequirementId = (
    arr
  ) => {
    return [
      ...new Map(
        arr.map((item) => [
          item.RequirementId,
          item,
        ])
      ).values(),
    ];
  };

  /*
   * ============================================================
   * EXCEL EXPORT
   * ============================================================
   */

  const onExportToExcel = async () => {
    try {
      const isHC =
        userRoles &&
        userRoles.includes(
          "HEALTHCARE"
        );

      const isED =
        userRoles &&
        (
          userRoles.includes(
            "EDUCATION"
          ) ||
          userRoles.includes(
            "FACULTY"
          )
        );

      if (!isHC && !isED) {
        console.warn(
          "No valid user roles found for export."
        );

        alert(
          "No valid user roles found for export."
        );

        return;
      }

      const searchData =
        tableData.filter((item) =>
          item?.contact?.name
            ?.toLowerCase()
            .includes(
              filterName.toLowerCase()
            )
        );

      const selectedDataToExport =
        isShowSelected
          ? searchData.filter((item) =>
              selectedItems.includes(
                item.id
              )
            )
          : selectedItems.length > 0
            ? searchData.filter((item) =>
                selectedItems.includes(
                  item.id
                )
              )
            : searchData;

      const CPNWRequirements =
        uniqueByRequirementId(
          selectedDataToExport.flatMap(
            (item) =>
              item.CPNWRequirements ||
              []
          )
        );

      const HCRequirements =
        uniqueByRequirementId(
          selectedDataToExport.flatMap(
            (item) =>
              item.HCRequirements ||
              []
          )
        );

      const EDRequirements =
        uniqueByRequirementId(
          selectedDataToExport.flatMap(
            (item) =>
              item.EDRequirements ||
              []
          )
        );

      const BGCRequirements =
        uniqueByRequirementId(
          selectedDataToExport.flatMap(
            (item) =>
              item.BackgroundCheckRequirements ||
              []
          )
        );

      const WatchRequirements =
        uniqueByRequirementId(
          selectedDataToExport.flatMap(
            (item) =>
              item.WatchCheckRequirements ||
              []
          )
        );

      const CPNWNonELearning =
        CPNWRequirements.filter(
          (r) =>
            r.CategoryName !==
            "eLearning"
        );

      const CPNWELearning =
        CPNWRequirements.filter(
          (r) =>
            r.CategoryName ===
            "eLearning"
        );

      const sortByName = (
        a,
        b
      ) =>
        (a.Name || "").localeCompare(
          b.Name || ""
        );

      CPNWNonELearning.sort(
        sortByName
      );

      EDRequirements.sort(
        sortByName
      );

      HCRequirements.sort(
        sortByName
      );

      BGCRequirements.sort(
        sortByName
      );

      WatchRequirements.sort(
        sortByName
      );

      const CPNWRequirementNames =
        CPNWNonELearning.map(
          formatDataHeaders
        );

      const EdRequirementNames =
        EDRequirements.map(
          formatDataHeaders
        );

      const HcRequirementNames =
        HCRequirements.map(
          formatDataHeaders
        );

      const BGCRequirementNames =
        BGCRequirements.map(
          formatDataHeaders
        );

      const WatchRequirementNames =
        WatchRequirements.map(
          formatDataHeaders
        );

      let headers = [
        { value: "Status" },
        { value: "Name" },
        { value: "School" },
        { value: "Program" },
        { value: "Location" },
        { value: "Start Date" },
      ];

      if (isED) {
        headers = [
          { value: "Name" },
          { value: "Email" },
          { value: "Program" },
        ];
      }

      headers = headers
        .concat(
          CPNWRequirementNames
        )
        .concat(
          EdRequirementNames
        )
        .concat(
          HcRequirementNames
        )
        .concat(
          BGCRequirementNames
        )
        .concat(
          WatchRequirementNames
        );

      if (
        CPNWELearning.length > 0
      ) {
        headers.push({
          value:
            "eLearning Modules",
          isELearningColumn: true,
        });
      }

      const wb =
        new ExcelJS.Workbook();

      const ws =
        wb.addWorksheet(
          "Sheet 1"
        );

      ws.addRow(
        headers.map(
          (item) =>
            item.value || item
        )
      );

      for (
        let i = 0;
        i <
        selectedDataToExport.length;
        i++
      ) {
        const item =
          selectedDataToExport[i];

        let rowData = [];

        if (isHC) {
          rowData =
            rowData.concat([
              {
                value:
                  item.assignmentStatus,
              },
              {
                value:
                  item.contact.name,
              },
              {
                value:
                  item.contact.school,
              },
              {
                value:
                  item.contact.program,
              },
              {
                value:
                  item.contact.location ||
                  "",
              },
              {
                value:
                  item.contact
                    .startDate
                    ? format(
                        item.contact
                          .startDate,
                        "MM/dd/yyyy"
                      )
                    : "",
              },
            ]);
        } else if (isED) {
          rowData =
            rowData.concat([
              {
                value:
                  item.contact.name,
              },
              {
                value:
                  item.contact.email ||
                  "",
              },
              {
                value:
                  item.contact.program,
              },
            ]);
        }

        for (const header of headers) {
          if (
            !header.requirementId &&
            !header.isELearningColumn
          ) {
            continue;
          }

          if (
            header.isELearningColumn
          ) {
            const eLearningModules =
              (
                item.CPNWRequirements ||
                []
              ).filter(
                (r) =>
                  r.CategoryName ===
                  "eLearning"
              );

            const eLearningLines =
              eLearningModules.map(
                (module) => {
                  const status =
                    module.ContactRequirementStatus ||
                    "";

                  const expiresOn =
                    formatDate(
                      module.ContactRequirementExpiresOn
                    ) || "--";

                  return `${module.Name} - Status: ${status} - Expiration Date: ${expiresOn}`;
                }
              );

            rowData.push({
              value:
                eLearningLines.join(
                  "\n"
                ),
              style: {},
            });
          } else {
            const requirements =
              (
                item.CPNWRequirements ||
                []
              )
                .concat(
                  item.HCRequirements ||
                    []
                )
                .concat(
                  item.EDRequirements ||
                    []
                )
                .concat(
                  item.BackgroundCheckRequirements ||
                    []
                )
                .concat(
                  item.WatchCheckRequirements ||
                    []
                );

            const req =
              requirements.find(
                (h) =>
                  h.RequirementId ===
                  header.requirementId
              );

            rowData.push(
              formatDataValues(
                req,
                header
              )
            );
          }
        }

        const values =
          rowData.map(
            (cell) => cell.value
          );

        const addedRow =
          ws.addRow(values);

        addedRow.eachCell(
          (
            cell,
            colNumber
          ) => {
            const style =
              rowData[
                colNumber - 1
              ]?.style;

            cell.alignment = {
              wrapText: true,
              vertical: "top",
              horizontal: "left",
            };

            if (style) {
              Object.getOwnPropertyNames(
                style
              ).forEach(
                (property) => {
                  cell[property] =
                    style[property];
                }
              );
            }
          }
        );
      }

      for (
        let i = 1;
        i <= headers.length;
        i++
      ) {
        const header =
          headers[i - 1];

        if (
          header.requirementId ||
          header.isELearningColumn
        ) {
          ws.getColumn(
            i
          ).width = 40;
        } else {
          ws.getColumn(
            i
          ).width = Math.max(
            header.value.length + 2,
            15
          );
        }
      }

      const headerRow =
        ws.getRow(1);

      headerRow.eachCell(
        (cell) => {
          cell.font = {
            bold: true,
          };

          cell.alignment = {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          };

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb: "FFE0E0E0",
            },
          };
        }
      );

      const buffer =
        await wb.xlsx.writeBuffer();

      const blob = new Blob(
        [buffer],
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      saveAs(
        blob,
        "Passports - CPNW.xlsx"
      );
    } catch (error) {
      console.error(
        "Export error:",
        error
      );
    }
  };

  /*
   * ============================================================
   * VIDEO MODAL
   * ============================================================
   */

  const onCloseVideoTutorialModal =
    () => {
      setOpenVideoTutorialModal(false);
      setVideoUrl(undefined);
    };

  const onOpenVideoTutorialModal = (
    isOpen,
    videoUrl
  ) => {
    setOpenVideoTutorialModal(
      isOpen
    );

    setVideoUrl(videoUrl);
  };

  /*
   * ============================================================
   * PASSPORT FILES
   * ============================================================
   */

  const handleGetPassports =
    async (contactId) => {
      setIsFileManagementModalOpen(
        true
      );

      setPassportFileModalLoading(
        true
      );

      try {
        const rsp =
          await onGetPassportsData(
            contactId
          );

        const passports =
          rsp?.data || [];

        setContactDataWithPassports(
          {
            contactId,
            passports,
          }
        );
      } catch (error) {
        console.error(
          "Error fetching passports:",
          error
        );
      } finally {
        setPassportFileModalLoading(
          false
        );
      }
    };

  /*
   * ============================================================
   * REQUIREMENT STATUS
   * ============================================================
   */

  const getStatus = (
    reqList
  ) => {
    /*
     * FIX:
     *
     * Original:
     *
     * StatusId !== 3 || StatusId !== 4
     *
     * is always true.
     *
     * Must use AND.
     */
    if (
      reqList.some(
        (item) =>
          item.StatusId !== 3 &&
          item.StatusId !== 4
      )
    ) {
      return "Incomplete";
    }

    if (
      reqList.some(
        (item) =>
          item.IsExpiring
      )
    ) {
      return "Expiring";
    }

    return "Complete";
  };

  const handleStatusRequirementByRole =
    (
      requirement,
      role
    ) => {
      const filtered =
        requirement.filter(
          (item) =>
            item.RequirementType ==
            role
        );

      if (
        filtered.length > 0
      ) {
        const status =
          getStatus(filtered);

        const result =
          filtered.map(
            (item) => ({
              Name: item.Name,
              Status:
                item.StatusId !== 3 &&
                item.StatusId !== 4
                  ? "Incomplete"
                  : item.IsExpiring
                    ? "Expiring"
                    : "Complete",
            })
          );

        return {
          status,
          result,
        };
      }

      return null;
    };

  const handleStatusRequirementByCategory =
    (
      requirement,
      category
    ) => {
      if (
        category === "BGC"
      ) {
        const filtered =
          requirement.filter(
            (item) =>
              item.Category?.Id ===
                9 ||
              item.Category?.Id ===
                11
          );

        if (
          filtered.length > 0
        ) {
          const status =
            getStatus(filtered);

          const result =
            filtered.map(
              (item) => ({
                Name: item.Name,
                Status:
                  item.StatusId !== 3 &&
                  item.StatusId !== 4
                    ? "Incomplete"
                    : item.IsExpiring
                      ? "Expiring"
                      : "Complete",
              })
            );

          return {
            status,
            result,
          };
        }
      } else if (
        category === "WATCH"
      ) {
        const filtered =
          requirement.filter(
            (item) =>
              item.Category?.Id ===
                10 ||
              item.Category?.Id ===
                12
          );

        if (
          filtered.length > 0
        ) {
          const status =
            getStatus(filtered);

          const result =
            filtered.map(
              (item) => ({
                Name: item.Name,
                Status:
                  item.StatusId !== 3 &&
                  item.StatusId !== 4
                    ? "Incomplete"
                    : item.IsExpiring
                      ? "Expiring"
                      : "Complete",
              })
            );

          return {
            status,
            result,
          };
        }
      }

      return null;
    };

  /*
   * ============================================================
   * ASSIGNMENT REVIEW
   * ============================================================
   */

  const handleAssignmentReview =
    async (
      assignmentId,
      reviewStatus,
      textForRejection
    ) => {
      try {
        const success =
          await assignmentReview(
            assignmentId,
            reviewStatus,
            textForRejection
          );

        if (success) {
          setReviewAssignmentId(
            null
          );
        }
      } catch (error) {
        console.log(error);
      }
    };

  /*
   * ============================================================
   * VIDEO BUTTON
   * ============================================================
   */

  const VideoPlayerButton = ({
    label,
    videoUrl,
  }) => {
    return (
      <div>
        {label && (
          <strong>
            {label}:{" "}
          </strong>
        )}

        <a
          className={
            styles.videoPopup
          }
          onClick={() =>
            onOpenVideoTutorialModal(
              true,
              videoUrl
            )
          }
        >
          <FontAwesomeIcon
            icon={faVideo}
            size="lg"
          />
        </a>
      </div>
    );
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div>
      {/*
       * ========================================================
       * FILTERS
       * ========================================================
       */}

      <div
        className={
          styles.selectItems
        }
      >
        {/* Location */}
        <div
          className={
            styles.selectItem
          }
        >
          <DashboardSelect
            placeholder="Locations - Select All"
            options={
              locationsOptions
            }
            tip="Click here to filter results by location"
            onSelect={
              onSelectLocation
            }
          />
        </div>

        {/* School / Program */}
        <div
          className={
            styles.selectItem
          }
        >
          <RequiredByDropdownED
            placeholder="Schools / Programs - Select All"
            options={
              requiredByEDOptions
            }
            tip="Click here to filter results by school and program"

            /*
             * IMPORTANT:
             *
             * Do NOT use selected={[]}.
             *
             * This preserves the selected
             * program checkboxes.
             */
            selected={
              selectedPrograms
            }

            onSelect={(value) => {
              console.log(
                "selected programs",
                value
              );

              onSelectSchool(
                value
              );
            }}
          />
        </div>

        {/* Clinical Start Date */}
        <div
          className={
            styles.selectItem
          }
        >
          <DashboardSelect
            placeholder="Clinical Start Date - Select All"
            options={
              startDatesOptions
            }
            tip="Click here to filter results by clinical start date"
            onSelect={
              onSelectStartDate
            }
          />
        </div>

        {/* Search */}
        <div
          className={
            styles.searchContent
          }
        >
          <DashboardSearch
            onInput={(input) => {
              setFilterName(
                input
              );

              setCurrentPage(1);
            }}
            searchDelay={0}
            minChar={0}
          />
        </div>
      </div>

      {/*
       * ========================================================
       * TABLE KEY
       * ========================================================
       */}

      <div
        className={
          styles.table_key
        }
      >
        <DashboardPassportTableKey
          onClick={() =>
            setIsSummaryReportItemModalOpen(
              true
            )
          }
        />
      </div>

      {/*
       * ========================================================
       * TUTORIALS
       * ========================================================
       */}

      <div
        className={
          styles.tutorialHeaderText
        }
      >
        Tutorials:
      </div>

      <div
        className={
          styles.tutorialItem
        }
      >
        <VideoPlayerButton
          label="Reporting"
          videoUrl="https://cpnw.blob.core.windows.net/documents/CoordinatorTutorials/Instructions/reportingHealthcare.mp4"
        />

        <span
          style={{
            padding:
              "0px 10px",
          }}
        >
          |
        </span>

        <div>
          <a
            href="https://cpnw.blob.core.windows.net/documents/CoordinatorTutorials/Instructions/HealthcareGuide.pdf"
            target="_blank"
            rel="noreferrer"
            className={
              styles.healthcareGuideText
            }
          >
            Healthcare Guide
            (PDF)
          </a>
        </div>
      </div>

      {/*
       * ========================================================
       * TABLE OPTIONS
       * ========================================================
       */}

      <div
        className={
          styles.tableOptionContainer
        }
      >
        <div
          className={
            styles.itemsPerPageContent
          }
        >
          <DashboardItemsPerPage
            disabled={false}
            limit={limit}
            limitOptions={
              limitOptions
            }
            onLimitChange={
              onLimitChange
            }
          />
        </div>

        <div
          className={
            styles.buttonContent
          }
        >
          <Button
            text="Show All"
            className={
              styles.button
            }
            onClick={() => {
              setIsShowSelected(
                false
              );

              setCurrentPage(1);
            }}
          />

          <Button
            text={`Show Selected${
              selectedItems.length >
              0
                ? ` (${selectedItems.length})`
                : ""
            }`}
            className={
              styles.button
            }
            onClick={() => {
              setIsShowSelected(
                true
              );

              setCurrentPage(1);
            }}
            disabled={
              selectedItems.length ===
              0
            }
          />

          <Button
            text="Export"
            className={
              styles.button
            }
            onClick={
              onExportToExcel
            }
          />
        </div>

        <div>
          <Button
            text="View columns names"
            data-reveal-id="reportCol"
            onClick={() =>
              setIsSummaryReportItemModalOpen(
                true
              )
            }
            className={
              styles.clickHereAnchor
            }
          />
        </div>
      </div>

      {/*
       * ========================================================
       * TABLE
       * ========================================================
       */}

      <div
        className={
          styles.table_wrapper
        }
      >
        <table
          className={
            styles.table
          }
        >
          <thead
            className={
              styles.theadContent
            }
          >
            <tr>
              <th
                className={
                  styles.thStyle
                }
              >
                <input
                  ref={
                    checkboxRef
                  }
                  type="checkbox"
                  checked={
                    allDisplayedSelected
                  }
                  onChange={
                    handleSelectAll
                  }
                  className={
                    styles.selectAllCheckbox
                  }
                  disabled={
                    !currentDisplayedItems ||
                    currentDisplayedItems.length ===
                      0
                  }
                />
              </th>

              {columns.map(
                ({
                  label,
                  accessor,
                  sortable,
                }) => {
                  const cl =
                    sortable
                      ? sortField ===
                          accessor &&
                        order ===
                          "asc"
                        ? "up"
                        : sortField ===
                            accessor &&
                          order ===
                            "desc"
                          ? "down"
                          : "default"
                      : "";

                  return (
                    <th
                      key={
                        accessor
                      }
                      onClick={
                        sortable
                          ? () =>
                              handleSortingChange(
                                accessor
                              )
                          : null
                      }
                      className={classNames(
                        styles.thStyle,
                        styles[cl]
                      )}
                    >
                      {label}
                    </th>
                  );
                }
              )}
            </tr>
          </thead>

          <tbody>
            {dataToShow
              .slice(
                startIndex,
                endIndex + 1
              )
              .map((item) => {
                const statusRequirement =
                  {
                    CPNW: {
                      status:
                        item.CPNWResult,
                      result:
                        (
                          item.CPNWRequirements ||
                          []
                        ).map(
                          (req) => ({
                            Name:
                              req.Name,
                            Status:
                              req.Result,
                          })
                        ),
                    },

                    Education: {
                      status:
                        item.EDResult,
                      result:
                        (
                          item.EDRequirements ||
                          []
                        ).map(
                          (req) => ({
                            Name:
                              req.Name,
                            Status:
                              req.Result,
                          })
                        ),
                    },

                    Healthcare: {
                      status:
                        item.HCResult,
                      result:
                        (
                          item.HCRequirements ||
                          []
                        ).map(
                          (req) => ({
                            Name:
                              req.Name,
                            Status:
                              req.Result,
                            FacilityId:
                              req.FacilityId,
                            FacilityName:
                              req.FacilityName,
                          })
                        ),
                    },

                    OIG: {
                      status:
                        item.OIGResult,
                    },

                    SAM: {
                      status:
                        item.SAMResult,
                    },

                    BGC: {
                      status:
                        item.BackgroundCheckResult,
                      result:
                        (
                          item.BackgroundCheckRequirements ||
                          []
                        ).map(
                          (req) => ({
                            Name:
                              req.Name,
                            Status:
                              req.Result,
                          })
                        ),
                    },

                    WATCH: {
                      status:
                        item.WatchCheckResult,
                      result:
                        (
                          item.WatchCheckRequirements ||
                          []
                        ).map(
                          (req) => ({
                            Name:
                              req.Name,
                            Status:
                              req.Result,
                          })
                        ),
                    },

                    ShowAssignmentReview:
                      item.ShowAssignmentReview,
                  };

                return (
                  <DashboardPassportItem
                    key={
                      item.isHC
                        ? `contact_${item.id}_assignment_${item.assignmentId}`
                        : `contact_${item.id}`
                    }
                    report={item}
                    columns={
                      columns
                    }

                    onContact={(
                      info
                    ) => {
                      const allReqs =
                        [
                          ...(
                            item.CPNWRequirements ||
                            []
                          ),
                          ...(
                            item.EDRequirements ||
                            []
                          ),
                          ...(
                            item.HCRequirements ||
                            []
                          ),
                          ...(
                            item.BackgroundCheckRequirements ||
                            []
                          ),
                          ...(
                            item.WatchCheckRequirements ||
                            []
                          ),
                        ];

                      const hasSubmissions =
                        allReqs.some(
                          (req) =>
                            req.StatusId ===
                              2 ||
                            req.StatusId ===
                              6
                        );

                      setCurrentItemInfo(
                        {
                          ...info,
                          hasSubmissions,
                        }
                      );

                      setIsContactInfoModalOpen(
                        true
                      );
                    }}

                    onLmClick={(
                      lmData
                    ) =>
                      setDataLearningModuleModal(
                        lmData
                      )
                    }

                    onSelect={(id) => {
                      if (
                        !selectedItems.includes(
                          id
                        )
                      ) {
                        setSelectedItems(
                          [
                            ...selectedItems,
                            id,
                          ]
                        );
                      } else {
                        setSelectedItems(
                          selectedItems.filter(
                            (
                              selectedId
                            ) =>
                              selectedId !==
                              id
                          )
                        );
                      }
                    }}

                    onReview={(
                      assignmentId,
                      status,
                      textForRejection
                    ) =>
                      handleAssignmentReview(
                        assignmentId,
                        status,
                        textForRejection
                      )
                    }

                    selected={selectedItems.includes(
                      item.id
                    )}

                    onFileClick={(
                      contactId
                    ) =>
                      handleGetPassports(
                        contactId
                      )
                    }

                    statusReq={
                      statusRequirement
                    }
                  />
                );
              })}
          </tbody>
        </table>
      </div>

      {/*
       * ========================================================
       * PAGINATION
       * ========================================================
       */}

      <div
        className={classNames(
          styles.assignmentPaginationRow,
          styles.row
        )}
      >
        <div
          className={classNames(
            styles.columnPagination,
            styles.column
          )}
        >
          <DashboardTablePagination
            onPageChange={(page) =>
              setCurrentPage(
                page
              )
            }
            total={
              totalItems
            }
            perPage={limit}
          />
        </div>
      </div>

      {/*
       * ========================================================
       * VIDEO MODAL
       * ========================================================
       */}

      <VideoTutorialModal
        isOpen={
          isOpenVideoTutorialModal
        }
        videoUrl={videoUrl}
      />

      {/*
       * ========================================================
       * CONTACT INFORMATION MODAL
       * ========================================================
       */}

      <DashboardContactInformationModal
        onClose={() =>
          setIsContactInfoModalOpen(
            false
          )
        }
        contact={
          currentItemInfo
        }
        isOpen={
          isContactInfoModalOpen
        }
        headerTitle="Contact Information"
        onViewRequirements={
          currentItemInfo?.hasSubmissions
            ? () => {
                const reviewRoute =
                  userRoles?.includes(
                    "HEALTHCARE"
                  )
                    ? "/Admin/HealthcareReview"
                    : "/Admin/Review";

                window.open(
                  `${reviewRoute}?contactId=${currentItemInfo.id}`,
                  "_blank"
                );
              }
            : undefined
        }
      />

      {/*
       * ========================================================
       * PASSPORT FILE MODAL
       * ========================================================
       */}

      {isFileManagementModalOpen && (
        <DashboardPassportFileModal
          isOpen={true}
          onClose={() =>
            setIsFileManagementModalOpen(
              false
            )
          }
          contactDataWithPassports={
            contactDataWithPassports
          }
          passportUpload={
            passportUpload
          }
          passportDelete={
            passportDelete
          }
          onDownloadContactPassports={
            onDownloadContactPassports
          }
          isLoading={
            passportFileModalLoading
          }
          showDelete={
            showDelete
          }
          showDownload={
            showDownload
          }
          showUpload={
            showUpload
          }
        />
      )}

      {/*
       * ========================================================
       * REPORT COLUMN MODAL
       * ========================================================
       */}

      <DashboardPassportReportItemModal
        children={
          <>
            <div>
              <ul className="no-bullet">
                <li>
                  <strong>
                    DOCS
                  </strong>{" "}
                  - Additional
                  Documents
                </li>

                <li>
                  <strong>
                    CPNW
                  </strong>{" "}
                  - CPNW Specific
                  Requirements
                </li>

                <li>
                  <strong>
                    HC
                  </strong>{" "}
                  - Healthcare Site
                  Specific
                  Requirements
                </li>

                <li>
                  <strong>
                    ED
                  </strong>{" "}
                  - Education Site
                  Specific
                  Requirements
                </li>
              </ul>
            </div>

            <div>
              <ul className="no-bullet">
                <li>
                  <strong>
                    OIG
                  </strong>{" "}
                  - OIG Excluded
                  Provider Check
                </li>

                <li>
                  <strong>
                    SAM
                  </strong>{" "}
                  - SAM Excluded
                  Provider Check
                </li>

                <li>
                  <strong>
                    BGC
                  </strong>{" "}
                  - Background
                  Check
                </li>

                <li>
                  <strong>
                    WATCH
                  </strong>{" "}
                  - WATCH Reports
                </li>
              </ul>
            </div>
          </>
        }
        isOpen={
          isSummaryReportItemModalOpen
        }
        onClose={() =>
          setIsSummaryReportItemModalOpen(
            false
          )
        }
      />
    </div>
  );
}

DashboardPassportTable.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
};

export default DashboardPassportTable;